import { AfterViewChecked, Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, signal, SimpleChanges, ViewChild, OnChanges, AfterContentChecked, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatSort } from '@angular/material/sort';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, Router } from '@angular/router';
import { format, formatDistanceToNowStrict, parseISO } from 'date-fns';
import snakeCase from 'lodash/snakeCase';
import { BehaviorSubject, combineLatest, Observable, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  first,
  map,
  scan,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { CreateTaskComponent } from '../create-task-dialog/create-task-dialog.component';
import { ConfirmDialog, DialogService } from '../../dialogs';
import { ApiParams, CoreApiService, queryAll } from '../../core-api.service';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectPermissions, ProjectsService } from '../../projects/projects.service';
import { RoutingHelperService } from '../../routing-helper.service';
import { downloadFile } from '../../shared/browser.utils';
import { OverflowMeasurement, calcChipOverflow } from '../../shared/chips.utils';
import { PickerProject } from '../../shared/components/project-picker/project-picker.component';
import { TaskParentContainerType, TaskStatus, TaskType, TaskViewer } from '../../shared/enums/deprecated-task.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { SiteRole } from '../../shared/enums/site-role.enum';
import { splitMultiFilterString } from '../../shared/filter.utils';
import { Task, BulkTaskInput, TaskStatusItem, TaskListColumn } from '../../shared/models/deprecated-task.model';
import { Option } from '../../shared/models/option.model';
import { ReadTaskProtocol } from '../../shared/models/read-task-protocol.model';
import { User } from '../../shared/models/user.model';
import { firstValueFrom, replayValue } from '../../shared/observable.utils';
import { TaskReviewComponent } from '../task-review-dialog/task-review-dialog.component';
import { TasksService } from '../tasks.service';
import { getHierarchyDisplay, getStatusItem, getTaskParentLabels, getTaskStatusDisplay } from '../tasks.utils';
import { TranslationService } from '../../translation.service';
import { OhifApp } from '../../viewers/ohifviewer/ohifviewer.utils';
import { ViewerService } from '../../viewers/viewer.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { ProjectsQueryService } from 'app/src/projects/projects-query.service';

interface DateItem {
  display: string;
  tooltip: string;
  isOverdue?: boolean;
  date: Date;
}

export interface TasksRow {
  _id: string;
  actions: string;
  assignee: User;
  container: {
    _id: string
    label: string
    hierarchy: string
    type: TaskParentContainerType
  };
  created: DateItem;
  creator: User;
  dueDate: DateItem;
  projectId: string;
  projectLabel: string;
  protocol?: ReadTaskProtocol;
  selected: boolean;
  status: TaskStatusItem;
  subType?: { label: string }; // TODO supported for only certain task types
  tags: string[];
  taskId: string;
  taskType: string;
  viewer: string;
}

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  host: {
    '[class.page-content]': 'isSiteContext',
    '[class.no-padding]': '!isSiteContext',
    'test-id': 'tasks-list',
  },
  selector: 'tasks-list',
  styleUrls: ['./tasks-list.component.scss'],
  templateUrl: './tasks-list.component.html',
  standalone: false,
})
export class TasksListComponent implements AfterViewChecked, OnDestroy, OnInit, OnChanges, AfterContentChecked {
  private config = inject(ConfigService);
  private dialog = inject(DialogService);
  private element = inject(ElementRef);
  private coreApi = inject(CoreApiService);
  private logger = inject(LoggingService);
  private notifications = inject(NotificationService);
  private profileService = inject(ProfileService);
  private projectsService = inject(ProjectsService);
  private tasksService = inject(TasksService);
  private translate = inject(TranslationService);
  private viewers = inject(ViewerService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private routingHelper = inject(RoutingHelperService);
  private projectQueryService = inject(ProjectsQueryService);

  @HostBinding('class.site-context') isSiteContext = false;

  readonly projectId = signal<string>(this.routingHelper.getGlobalRouteParams().projectId);

  readonly projectQuery = this.projectQueryService.injectProjectQuery(this.projectId);

  @ViewChild(MatSort) matSort: MatSort;
  @ViewChild('fwLoadTrigger') loadTrigger: ElementRef;

  allProjectIds: string[] = [];
  allTasksSelected = false;
  clearFilterSortTooltip: string;
  authorizedProjects: Record<string, boolean> = {};
  cachedProjects: PickerProject[] = [];
  canDeleteSelection = false;
  canManage = false;
  canReadTaskReview = false;
  selectionValidViewMultiple = false;
  readonly columns = [
    'checkbox',
    TaskListColumn.Project,
    TaskListColumn.TaskId,
    TaskListColumn.TaskType,
    TaskListColumn.Container,
    TaskListColumn.Assignee,
    TaskListColumn.Creator,
    TaskListColumn.Created,
    TaskListColumn.DueDate,
    TaskListColumn.Status,
    TaskListColumn.Tag,
    'actions',
  ];
  disabledColumns = [
    { key: TaskListColumn.Project, enabled: true, label: 'common.labels.project' },
    { key: TaskListColumn.TaskId, enabled: true, label: 'tasks.labels.taskId' },
    { key: TaskListColumn.TaskType, enabled: true, label: 'tasks.labels.taskType' },
    { key: TaskListColumn.Container, enabled: true, label: 'common.labels.container' },
    { key: TaskListColumn.Assignee, enabled: true, label: 'common.labels.assignee' },
    { key: TaskListColumn.Creator, enabled: true, label: 'tasks.labels.creator' },
    { key: TaskListColumn.Created, enabled: true, label: 'common.labels.created' },
    { key: TaskListColumn.DueDate, enabled: true, label: 'tasks.labels.dueDate' },
    { key: TaskListColumn.Status, enabled: true, label: 'common.labels.status' },
    { key: TaskListColumn.Tag, enabled: true, label: 'common.labels.tags' },
  ];
  currentUser: string;
  displayedColumns: string[] = [];
  displayedColumnsCached: string[] = [];
  downloadableProjects: string[] = [];
  filters = new FormGroup({
    projectIds: new FormControl([] as string[]),
    taskId: new FormControl(''),
    taskType: new FormControl(''),
    container: new FormControl(''),
    assignee: new FormControl(''),
    creator: new FormControl(''),
    created: new FormControl(''),
    dueDate: new FormControl(''),
    status: new FormControl(''),
    containerId: new FormControl(''),
    protocolId: new FormControl(''),
    tags: new FormControl([]),
    tagText: new FormControl(''),
  });
  readonly loadMore$ = new Subject<void>();
  hasData = true;
  hasLoadingError = false;
  initialEmission = true;
  isLoading = false;
  isSiteAdmin = false;
  labels: Record<string, string>;
  loadedTasks: { [key: string]: TasksRow } = {};
  nextAfterId: string = null;
  permissions: ProjectPermissions = {};
  readTaskProtocols: ReadTaskProtocol[] = [];
  readonly removeTasks$ = new BehaviorSubject<TasksRow[]>([]);
  reviewerAuthorizedProjects: string[] = [];
  selectableTasksPresent = false;
  shouldShowClearFiltersSort = false;
  someTasksSelected = false;
  statusOptions: SelectOption[] = Object.entries(TaskStatus).map(([key, value]) => ({
    label: getTaskStatusDisplay(TaskStatus[key]),
    value,
  }));
  tagOverflows: OverflowMeasurement[] = [];
  tasksData$: Observable<TasksRow[]>;
  readonly tasksSort$ = new BehaviorSubject<Sort>({ active: 'created', direction: 'desc' });
  TaskStatus = TaskStatus;
  taskTypeOptions: SelectOption[] = Object.entries(TaskType).map(([key, value]) => ({ label: key, value }));
  userOptions: (User & Option)[] = [];
  viewFilter: string;

  private subscriptions = {
    getProjectsPermissions: Subscription.EMPTY,
    initData: Subscription.EMPTY,
    projectFilter: Subscription.EMPTY,
    projects: Subscription.EMPTY,
    refresh: Subscription.EMPTY,
    settings: Subscription.EMPTY,
    usersAndProtocols: Subscription.EMPTY,
  };
  private users: User[] = [];

  get assigneeFilter(): string {
    return this.filters.get('assignee').value;
  }

  set assigneeFilter(value: string | string[]) {
    this.filters.get('assignee').setValue(value.toString());
  }

  get creatorFilter(): string {
    return this.filters.get('creator').value;
  }

  set creatorFilter(value: string | string[]) {
    this.filters.get('creator').setValue(value.toString());
  }

  get projectFilter(): PickerProject[] {
    return this.cachedProjects;
  }

  set projectFilter(projects: PickerProject[]) {
    this.cachedProjects = projects;
    this.filters.get('projectIds').setValue(projects.map((project) => project._id));
  }

  constructor() {
    const projectsService = this.projectsService;

    this.subscriptions.getProjectsPermissions = projectsService.getProjectsWithPermissions([PermissionAction.FilesDownload])
      .subscribe(downloadableProjects => {
        this.downloadableProjects = downloadableProjects.map(p => p._id);
      });
  }

  ngOnInit(): void {

    this.isSiteContext = this.router.url.startsWith('/tasks') || this.router.url.startsWith('/legacy-tasks');

    const labels$ = combineLatest([
      this.translate.getBundle('common.labels'),
      this.translate.getBundle('tasks.labels'),
    ]).pipe(
      tap(([commonLabels, tasksLabels]) => {
        this.labels = {
          acquisition: commonLabels.get('acquisition'),
          ascending: commonLabels.get('ascending'),
          assignee: commonLabels.get('assignee'),
          container: commonLabels.get('container'),
          creator: tasksLabels.get('creator'),
          created: commonLabels.get('created'),
          descending: commonLabels.get('descending'),
          dueDate: tasksLabels.get('dueDate'),
          file: commonLabels.get('file'),
          filterByRelated: tasksLabels.get('filterByRelated'),
          group: commonLabels.get('group'),
          none: commonLabels.get('none'),
          project: commonLabels.get('project'),
          projects: commonLabels.get('projects'),
          protocol: commonLabels.get('protocol'),
          session: commonLabels.get('session'),
          sortedBy: commonLabels.get('sortedBy'),
          status: commonLabels.get('status'),
          subject: commonLabels.get('subject'),
          taskId: tasksLabels.get('taskId'),
          more: tasksLabels.get('more'),
        };
        this.taskTypeOptions.unshift({ label: this.labels.none, value: null });
        this.statusOptions.unshift({ label: this.labels.none, value: null });
      }),
    );
    const protocolParams = this.projectId() ? { filter: `parents.project=${this.projectId()}` } : {};
    this.subscriptions.initData = combineLatest([
      combineLatest([
        this.profileService.getProfile().pipe(first(data => Boolean(data))),
        queryAll(this.coreApi.users.query),
        queryAll(() => this.coreApi.readTaskProtocols.query(protocolParams, true)),
        this.projectsService.projectPermissions$.pipe(first()),
        this.projectsService.getProjects().pipe(first()),
      ]),
      combineLatest([
        this.activatedRoute.queryParams,
        labels$,
      ]),
    ]).subscribe(([[profile, users, readTaskProtocols, permissions, projects], [qParams]]) => {
      if (this.isLoading) {
        return;
      }
      if (this.initialEmission) {
        this.initialEmission = false;
        this.currentUser = profile._id;
        this.isSiteAdmin = Boolean(profile && SiteRole.isSiteAdmin(profile));
        this.users = users;
        this.readTaskProtocols = readTaskProtocols;
        this.permissions = permissions;
        this.allProjectIds = projects.map(project => project._id);
        this.updatePermissions();
      }
      this.displayedColumns = this.isSiteContext ? [...this.columns] : this.columns.filter((column) => column !== 'project');
      this.displayedColumnsCached = [...this.displayedColumns];
      // filter disabled columns
      this.displayedColumns = this.displayedColumns.filter(c => this.filterDisabledColumns(c));
      this.clearLoadedTasks();
      const { view, project, protocol, containerId } = qParams;
      this.viewFilter = !['all', 'assigned', 'created'].includes(view)
        ? (this.isSiteAdmin || project || protocol || containerId) ? 'all' : 'assigned'
        : view;
      this.selectViewFilter(this.viewFilter);
      if (this.isSiteContext && project) {
        // push off the stack so project-picker.component is rendered before setting projectFilter
        setTimeout(() => {
          const project$ = toObservable(this.projectQuery.data);
          this.subscriptions.projectFilter = combineLatest([
            project$,
            this.projectsService.groups$,
          ]).subscribe(([project, groups]) => {
            const group = groups.find((v) => v._id === project.group);
            this.projectFilter = [{
              _id: project._id,
              label: project.label,
              groupLabel: group.label,
              groupId: project.group,
            }];
          });
        });
      }
      this.filters.get('protocolId').setValue(protocol || '');
      this.filters.get('containerId').setValue(containerId || '');
      if (containerId?.length > 0) {
        // clear filters unrelated to Viewing Related tasks
        this.filters.get('taskId').reset('');
        this.filters.get('assignee').reset('');
        this.filters.get('creator').reset('');
        this.filters.get('status').reset('');
        this.filters.get('tags').reset([]);
      }
      this.loadData();
    });
    this.subscriptions.refresh = this.tasksService.refreshTaskList$.subscribe(() => {
      // Refresh the tasks count in the sidebar
      this.profileService.getUserTaskCount({ refresh: true }).pipe(first()).subscribe();
      this.loadData();
    });
    this.subscriptions.settings = this.profileService.settings$.subscribe(settings => {
      if (Array.isArray(settings.tasks_columns_disabled)) {
        // default server val is [], which should display all columns
        for (const column of this.disabledColumns) {
          column.enabled = settings.tasks_columns_disabled.length > 0
            ? !settings.tasks_columns_disabled.includes(column.key)
            : true;
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.projectId) {
      this.updatePermissions();
    }
  }

  ngAfterViewChecked(): void {
    this.loadMore();
  }

  ngAfterContentChecked(): void {
    this.calcOverflows();
  }

  @HostListener('window:resize', ['$event'])
  public calcOverflows(): void {
    const items = this.element.nativeElement.querySelectorAll('.chip-list');
    this.tagOverflows = calcChipOverflow(items);
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  get enabledColumns(): string[] {
    return this.displayedColumns.filter(c => this.filterDisabledColumns(c));
  }

  getShouldShowClearFiltersSort(queryParams: ApiParams): boolean {
    const projectFilter = `parents.project=${this.projectId()}`;
    const assigneeFilter = `assignee=${this.currentUser}`;
    const projectAssigneeFilter = `${projectFilter},${assigneeFilter}`;
    const creatorFilter = `origin.id=${this.currentUser}`;
    const projectCreatorFilter = `${projectFilter},${creatorFilter}`;
    const isAllView = this.viewFilter === 'all';
    const isAssignedView = this.viewFilter === 'assigned';
    const isCreatedView = this.viewFilter === 'created';
    const isDefaultSort = queryParams.sort === 'created:desc';
    return (this.isSiteContext
      ? (isAllView && queryParams.filter !== '')
      || (isAssignedView && queryParams.filter !== assigneeFilter)
      || (isCreatedView && queryParams.filter !== creatorFilter)
      : (isAllView && queryParams.filter !== projectFilter)
      || (isAssignedView && queryParams.filter !== projectAssigneeFilter)
      || (isCreatedView && queryParams.filter !== projectCreatorFilter)
    ) || !isDefaultSort;
  }

  selectRow(checked: boolean, row: TasksRow): void {
    this.loadedTasks[row._id].selected = checked;
    this.setRowsSelected();
  }

  setRowsSelected(): void {
    let foundUnselected = false;
    let foundSelected = false;
    const selectedTasks = Object.values(this.loadedTasks).filter(task => task.selected);
    for (const task of Object.values(this.loadedTasks)) {
      if (!task.selected) {
        foundUnselected = true;
        break;
      }
    }
    for (const task of Object.values(this.loadedTasks)) {
      if (task.selected) {
        foundSelected = true;
        break;
      }
    }
    this.allTasksSelected = !foundUnselected && foundSelected;
    this.someTasksSelected = foundUnselected && foundSelected;
    // Only show if we have multiple selected and all have the same container _id
    this.selectionValidViewMultiple = selectedTasks.length > 1 && selectedTasks.every(task => task.container?._id === selectedTasks[0].container?._id);
    this.canReadTaskReview = selectedTasks.length > 0 && selectedTasks.map(task => task.projectId).every(projectId => this.reviewerAuthorizedProjects.includes(projectId));
    const readerAdminProjects = Object.entries(this.authorizedProjects).filter(([, val]) => val).map(([key]) => key);
    this.canDeleteSelection = selectedTasks.length > 0 && selectedTasks.map(task => task.projectId).every(projectId => readerAdminProjects.includes(projectId));
  }

  getSelectedTasks(): TasksRow[] {
    return Object.values(this.loadedTasks).filter((task) => task.selected);
  }

  formatFilterSortTooltip(filters: string, sort: string): string {
    const items = splitMultiFilterString(filters).map((filter) => filter
      .replace(
        (this.isSiteContext
          ? /^parents\.project=\|\[.+\]/
          : /^parents\.project=.+/),
        (this.isSiteContext
          // replaces project ids with project labels
          ? `${this.labels.projects}: ${(filter.match(/\[(.+)\]/) || [])[1]?.split(',').map((id) => this.cachedProjects.find((project) => project._id === id)?.label).join(', ')}`
          : ''),
      )
      .replace(/^parents\.project=.+/, '')
      .replace(/^protocol_id=.*/, `${this.labels.protocol}: ${
        // replace protocol id with protocol label
        [(filter.match(/^protocol_id=(.*)/) || [])[1]].map((id) => this.readTaskProtocols.find((protocol) => protocol._id === id)?.label)[0]
        }`)
      .replace(/^assignee/, `${this.labels.assignee}:`)
      .replace(/^origin\.id/, `${this.labels.creator}:`)
      .replace(/^parent\.id=.*/, `${this.labels.container}: ${
        // replace parent id with parent label
        // assume all displayed tasks have the same parent if we have a containerId
        Object.values(this.loadedTasks).length > 0
          ? Object.values(this.loadedTasks)[0].container.label
          : (filter.match(/^parent\.id=(.*)/)?.[1]) || ''
        }`)
      .replace(/^status/, `${this.labels.status}:`)
      .replace(/^task_id/, `${this.labels.taskId}:`)
      .replace(/(=~)|(=)/, ' '),
    );
    items.push(`${this.labels.sortedBy} ${sort
      .replace(/task_id/, `'${this.labels.taskId}'`)
      .replace(/assignee/, `'${this.labels.assignee}'`)
      .replace(/creator/, `'${this.labels.creator}'`)
      .replace(/created/, `'${this.labels.created}'`)
      .replace(/due_date/, `'${this.labels.dueDate}'`)
      .replace(/status/, `'${this.labels.status}'`)
      .replace(/asc/, this.labels.ascending.toLowerCase())
      .replace(/desc/, this.labels.descending.toLowerCase())
      .replace(/:/, ' ')
      } `);
    if (filters.includes('parent.id=')) {
      items.unshift(this.labels.filterByRelated);
    }
    return items.filter((v) => Boolean(v)).join('\n').trim();
  }

  clearLoadedTasks(): void {
    this.setAllTasksSelected(false);
    this.loadedTasks = {};
  }

  setAllTasksSelected(checked: boolean): void {
    for (const task of Object.values(this.loadedTasks)) {
      if (this.authorizedProjects[task.projectId]) {
        task.selected = checked;
      }
    }
    this.setRowsSelected();
  }

  setPermissions(permissions: ProjectPermissions): void {
    // reviewing multiple tasks in the OHIF viewer requires multiple permissions
    const reviewerProjects = [];
    const reviewerPermissions = [PermissionAction.ReaderTaskAdmin, PermissionAction.AnnotationsViewOthers, PermissionAction.FormResponsesViewOthers];
    for (const [projectId, projectPermissions] of Object.entries(permissions)) {
      this.authorizedProjects[projectId] = projectPermissions.includes(PermissionAction.ReaderTaskAdmin);
      if (reviewerPermissions.every(permission => projectPermissions.includes(permission))) {
        reviewerProjects.push(projectId);
      }
    }
    this.reviewerAuthorizedProjects = reviewerProjects;
    this.canManage = Object.values(this.authorizedProjects).some(val => val);
    if (!this.canManage) {
      this.displayedColumns.splice(this.displayedColumns.indexOf('checkbox'), 1);
      this.displayedColumns.splice(this.displayedColumns.indexOf('actions'), 1);
    }
  }

  updatePermissions(): void {
    if (this.isSiteAdmin) {
      const fullPermissions = {};
      for (const projectId of this.allProjectIds) {
        fullPermissions[projectId] = Object.values(PermissionAction);
      }
      return this.setPermissions(fullPermissions);
    }
    const projectIds = this.isSiteContext
      ? Object.keys(this.permissions)
      : this.projectId() ? [this.projectId()] : [];
    const relevantPermissions = {};
    for (const [key, value] of Object.entries(this.permissions)) {
      if (projectIds.includes(key)) {
        relevantPermissions[key] = value;
      }
    }
    this.setPermissions(relevantPermissions);
  }

  loadMore(): void {
    // ensures continuous loading in the case the loadTrigger element is still visible after initial fetch for more data
    const triggerTop = this.loadTrigger.nativeElement.getBoundingClientRect().top;
    const triggerParentBottom = this.loadTrigger.nativeElement.parentElement.getBoundingClientRect().bottom;
    const triggerIsVisible = triggerTop < triggerParentBottom;
    if (!this.isLoading && this.nextAfterId && triggerIsVisible) {
      this.loadMore$.next();
    }
  }

  loadData(): void {
    this.isLoading = true;
    this.tasksData$ = combineLatest([
      this.profileService.exhaustiveMode$,
      replayValue(this.filters).pipe(debounceTime(200), tap(() => this.clearLoadedTasks())),
      this.tasksSort$.pipe(
        map((sort) => ({ ...sort, active: snakeCase(sort.active) })),
        tap(() => this.clearLoadedTasks()),
      ),
    ]).pipe(
      switchMap(([exhaustive, filterValues, sortValue]) => {
        this.userOptions = this.users.map((user) => {
          return {
            ...user,
            label: `${user.firstname} ${user.lastname}`,
            value: user._id,
          };
        });
        const queryParams: ApiParams = {
          limit: 10,
          filter: this.getFilterString(filterValues),
          sort: `${sortValue.active}:${sortValue.direction}`,
          exhaustive,
        };
        this.nextAfterId = null;
        return this.loadMore$.pipe(
          debounceTime(5), // avoid infinite call loop
          filter(() => Boolean(this.nextAfterId)),
          startWith(undefined as void),
          switchMap(() => {
            this.isLoading = true;
            return this.coreApi.readerTasks.query({
              ...queryParams,
              ...(this.nextAfterId ? { after_id: this.nextAfterId } : {}),
            });
          }),
          scan((allTasks, newPage) => {
            if (this.allTasksSelected && newPage.results.length > 0) {
              this.allTasksSelected = false;
              this.someTasksSelected = true;
            }
            const combined = allTasks.concat(newPage.results);
            this.nextAfterId = combined.length < newPage.total && newPage.results.length > 0
              ? newPage.results[newPage.results.length - 1]._id
              : null;
            return combined;
          }, []),
          map((tasks) => {
            this.hasData = tasks.length > 0;
            this.isLoading = false;
            this.selectableTasksPresent = tasks.some((task) => this.authorizedProjects[task.parents.project]);
            return tasks.map((task) => this.getTasksRow(task));
          }),
          switchMap((tasks) => {
            // to update the data in the table w/o rerunning the query
            return this.removeTasks$.pipe(
              switchMap((tasksToRemove) => {
                for (const taskToRemove of tasksToRemove) {
                  delete this.loadedTasks[taskToRemove._id];
                  const index = tasks.indexOf(taskToRemove);
                  if (index >= 0) {
                    tasks.splice(index, 1);
                  }
                }
                return of(tasks);
              }),
              tap((tasks) => {
                let nextAfterExists = false;
                for (const task of Object.values(tasks)) {
                  this.loadedTasks[task._id] = task;
                  if (task._id === this.nextAfterId) {
                    nextAfterExists = true;
                  }
                }
                // if the task with nextAfterId was deleted, use the last task on the page
                if (nextAfterExists === false && this.nextAfterId !== null) {
                  this.nextAfterId = tasks.length > 0 ? tasks.slice(-1)[0]._id : null;
                }
                this.setRowsSelected();
                this.shouldShowClearFiltersSort = this.getShouldShowClearFiltersSort(queryParams);
                this.clearFilterSortTooltip = this.formatFilterSortTooltip(queryParams.filter as string, queryParams.sort as string);
              }),
            );
          }),
        );
      }),
      catchError((error) => {
        this.hasLoadingError = true;
        this.isLoading = false;
        this.logger.error(error);
        this.notifications.error('tasks.get.error');
        return of([] as TasksRow[]);
      }),
    );
  }

  getFilterString(filterValues: { [key: string]: string | string[] }): string {
    const queryFilter = [];
    const showAssignedToMe = this.viewFilter === 'assigned';
    const showCreatedByMe = this.viewFilter === 'created';
    if (!this.isSiteContext) {
      queryFilter.push(`parents.project=${this.projectId()}`);
    } else if (filterValues.projectIds?.length > 0) {
      queryFilter.push(`parents.project=|[${(filterValues.projectIds as string[]).join(',')}]`);
    }
    if (filterValues.taskId) {
      queryFilter.push(`task_id=~${filterValues.taskId}`);
    }
    if (filterValues.taskType) {
      queryFilter.push(`task_type=${filterValues.taskType}`);
    }
    if (filterValues.protocolId) {
      queryFilter.push(`protocol_id=${filterValues.protocolId}`);
    }
    if (filterValues.assignee || showAssignedToMe) {
      queryFilter.push(`assignee=${showAssignedToMe ? this.currentUser : filterValues.assignee}`);
    }
    if (filterValues.creator || showCreatedByMe) {
      queryFilter.push(`origin.id=${showCreatedByMe ? this.currentUser : filterValues.creator}`);
    }
    if (filterValues.status) {
      queryFilter.push(`status=${filterValues.status}`);
    }
    if (filterValues.containerId) {
      queryFilter.push(`parent.id=${filterValues.containerId}`);
    }
    if (filterValues.tags?.length > 0) {
      queryFilter.push(`tags=|[${(filterValues.tags as string[]).join(',')}]`);
    }
    return queryFilter.join(',');
  }

  getTasksRow(task: Task): TasksRow {
    const parentLabels = getTaskParentLabels(task);
    return this.loadedTasks[task._id] || {
      _id: task._id,
      actions: '',
      assignee: this.users.find((user) => user._id === task.assignee),
      container: {
        _id: task.parent.id,
        label: parentLabels[task.parent.type],
        hierarchy: getHierarchyDisplay(this.labels, parentLabels),
        type: task.parent.type,
      },
      created: this.getCreatedDateItem(task.created),
      creator: this.users.find((user) => user._id === task.origin?.id),
      dueDate: this.getDueDateItem(task.due_date, task.status),
      projectId: task.parents.project,
      projectLabel: parentLabels.project,
      protocol: this.readTaskProtocols.find((protocol) => protocol._id === task.protocol_id),
      selected: Boolean(this.loadedTasks[task._id]?.selected),
      status: getStatusItem(task.status),
      tags: task.tags,
      taskId: task.task_id,
      taskType: task.task_type && this.getTaskType(task.task_type),
      viewer: task.viewer,
    };
  }

  getTaskType(taskType: TaskType): string {
    return Object.entries(TaskType).find(([, value]) => value === taskType)[0];
  }

  getTaskTypeDisplay(element: TasksRow): string {
    const output = [element.taskType];
    if (element.taskType) {
      if (element.protocol) {
        output.push(element.protocol?.label);
      } else if (element.subType) {
        output.push(element.subType?.label);
      }
    }
    return output.join(' / ');
  }

  getCreatedDateItem(dateString: string): DateItem {
    const date = parseISO(dateString);
    return {
      display: `${formatDistanceToNowStrict(date)} ago`,
      tooltip: format(date, 'MMM dd, yyyy hh:mm a'),
      date,
    };
  }

  getDueDateItem(dateString: string, status: TaskStatus): DateItem {
    const date = parseISO(dateString || '');
    const pastDueDate = parseISO(dateString || '');
    // Users should have until end of day to finish their work
    pastDueDate.setUTCHours(23, 59, 59, 999);
    const isOverdue = !dateString ? false : (status !== TaskStatus.Complete && pastDueDate < new Date());
    return {
      display: !dateString ? '' : format(date, 'MMM dd, yyyy'),
      tooltip: !dateString ? '' : isOverdue ? `${formatDistanceToNowStrict(date)} past due` : format(date, 'MMM dd, yyyy'),
      isOverdue,
      date,
    };
  }

  changeView(view: string): void {
    // clear all filters and reset sort on each view: 'all', 'assigned', and 'created by me'
    this.filters.setValue({
      projectIds: this.projectId() ? [this.projectId()] : [],
      taskId: '',
      taskType: '',
      container: '',
      assignee: '',
      creator: '',
      created: '',
      dueDate: '',
      status: '',
      containerId: '',
      protocolId: '',
      tags: [],
      tagText: '',
    });
    this.projectFilter = [];
    // defer sort for router state change
    setTimeout(() => this.tasksSort$.next({ active: 'created', direction: 'desc' }));

    this.router.navigate(['.'], {
      relativeTo: this.activatedRoute,
      queryParams: { view },
    })
  }

  selectViewFilter(view: string): void {
    this.viewFilter = view;
    this.displayedColumns = [...this.displayedColumnsCached].filter(c => this.filterDisabledColumns(c));
    if (this.viewFilter === 'assigned') {
      this.displayedColumns.splice(this.displayedColumns.indexOf('assignee'), 1);
    } else if (this.viewFilter === 'created') {
      this.displayedColumns.splice(this.displayedColumns.indexOf('creator'), 1);
    }
  }

  async deleteTasks(tasks: TasksRow[]): Promise<void> {
    const bundle = await firstValueFrom(this.translate.getBundle('tasks.dialog'));
    const taskIds = tasks.map((task) => task._id);
    this.dialog.open(ConfirmDialog, {
      data: {
        content: tasks.length > 1 ? bundle.get('deleteMany') : bundle.get('deleteOne'),
      },
    }).afterClosed().pipe(
      filter(proceed => Boolean(proceed)),
      switchMap(() => this.coreApi.bulk.tasks.delete({ task_ids: taskIds })),
    ).subscribe({
      next: result => {
        if (result.error.length > 0) {
          return this.notifications.error('tasks.delete.error');
        }
        this.notifications.success('tasks.delete.success');
        if (result.success.length > 0) {
          this.removeTasks$.next(tasks);
        }
        this.tasksService.refreshTaskList$.next();
      },
      error: (error) => {
        this.logger.error(error);
        this.notifications.error('tasks.delete.error');
      },
    });
  }

  newTasks(initialValue?: BulkTaskInput): void {
    this.dialog.open(CreateTaskComponent, {
      disableClose: true,
      hasBackdrop: true,
      position: { right: '0' },
      panelClass: 'border-radius-0',
      data: initialValue || { project_id: this.projectId() },
    }).afterClosed().subscribe((taskTemplate: BulkTaskInput) => {
      if (taskTemplate) {
        // relaunch the CreateTask dialog with the provided taskinput
        setTimeout(() => {
          this.newTasks(taskTemplate);
        }, 1000);
      }
    });
  }

  viewSelectedTasks(): void {
    const tasks = Object.values(this.loadedTasks).filter(task => task.selected);
    const viewers = new Set(tasks.map(task => task.viewer));
    if (viewers.size > 1) {
      this.notifications.error('tasks.launch.multiTask.error');
    } else if (viewers.has(TaskViewer.OHIFV3) && tasks.length > 1) {
      this.notifications.error('tasks.launch.v3MultiTask.error');
    } else {
      let app;

      if (viewers.has(TaskViewer.OHIF)) {
        app = OhifApp.OhifReact;
      } else if (viewers.has(TaskViewer.OHIFV3)) {
        app = OhifApp.OhifViewerV3;
      }

      const taskIds = tasks.map(task => task._id);
      this.viewers.ohifViewerDialog({ taskId: taskIds.join(','), app });
    }
  }

  viewTask(taskId: string, viewer: string): void {
    let app;

    if (viewer === TaskViewer.OHIFV3) {
      app = OhifApp.OhifViewerV3;
    } else if (viewer === TaskViewer.OHIF) {
      app = OhifApp.OhifReact;
    }

    this.viewers.ohifViewerDialog({ taskId: taskId, app }).subscribe(() => this.tasksService.refreshTaskList$.next());
  }

  editTask(taskRow: TasksRow): void {
    this.dialog.open(TaskReviewComponent, {
      disableClose: false,
      position: { right: '0' },
      panelClass: 'border-radius-0',
      data: { taskRow },
    });
  }

  canDownloadFiles(projectId: string): boolean {
    return this.downloadableProjects.includes(projectId);
  }

  downloadTaskFiles(task: { container: { type: string; _id: string; }; taskId: string; }): void {
    if (task.container.type === 'file') {
      this.coreApi.files.ticket(task.container._id).subscribe({
        next: ({ ticket, filename }) => {
          downloadFile(filename, this.config.getApiUrl(`/api/files/${task.container._id}/download?ticket=${ticket}`));
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('download.files.error');
        },
      });
    } else {
      this.coreApi.downloads.createReadTaskTicket({
        optional: true,
        nodes: [{ level: task.container.type, _id: task.container._id }],
      }, task.taskId).subscribe({
        next: ({ filename, ticket }) => {
          downloadFile(filename, this.config.getApiUrl(`/api/download?ticket=${ticket}&format=zip`));
        },
        error: error => {
          this.logger.error(error);
          this.notifications.error('download.files.error');
        },
      });
    }
  }

  addTagFilter(event: MatChipInputEvent): void {
    // clear the text input
    this.filters.controls.tagText.setValue('');
    const nextValue: string[] = this.filters.controls.tags.value;
    if (event.value === '') {
      return;
    }
    nextValue.push(event.value);
    this.filters.controls.tags.setValue(nextValue);
  }

  removeTagFilter(tagIndex: number): void {
    const { tags } = this.filters.controls;
    tags.setValue([
      ...tags.value.slice(0, tagIndex),
      ...tags.value.slice(tagIndex + 1),
    ]);
  }

  persistColumns(): void {
    // use setTimeout to wait for mat-checkbox afterChange
    setTimeout(() => {
      const allDisabled = this.disabledColumns.every(column => !column.enabled);
      if (allDisabled) {
        this.disabledColumns.forEach(column => column.enabled = true);
      }
      this.displayedColumns = [...this.displayedColumnsCached].filter(c => this.filterDisabledColumns(c));
      const columns = this.disabledColumns.filter(column => !column.enabled).map(column => column.key);
      this.profileService.updateSettings({ tasks_columns_disabled: columns });
    }, 0);
  }

  filterDisabledColumns(column: string): boolean {
    const togglableColumn = this.disabledColumns.find(disabledColumn => disabledColumn.key === column);
    if (!togglableColumn) {
      return true; // if we can't find the column (ex. checkbox, action) it isn't user disabled
    }
    return togglableColumn.enabled;
  }
}
